import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '@/core/auth/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  connectionError: null
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  useEffect(() => {
    // Only connect if user is authenticated
    if (!user) {
      // Disconnect if user logs out
      if (socketRef.current) {
        console.log('User logged out, disconnecting WebSocket');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // If already connected, don't reconnect
    if (socketRef.current?.connected) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error('No auth token available for WebSocket connection');
      setConnectionError('Authentication token not found');
      return;
    }

    console.log('Initializing WebSocket connection...');
    
    // Use the base URL directly (it already points to the backend server)
    const socketUrl = API_BASE_URL;
    
    const newSocket = io(socketUrl, {
      auth: {
        token
      },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('🔌 WebSocket connected successfully');
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttempts.current = 0;
    });

    newSocket.on('disconnect', (reason) => {
      console.log(`🔌 WebSocket disconnected: ${reason}`);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect with new token
        setConnectionError('Server disconnected. Reconnecting...');
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      reconnectAttempts.current++;
      
      if (error.message.includes('Authentication')) {
        setConnectionError('Authentication failed. Please log in again.');
        // Don't attempt to reconnect on auth errors
        newSocket.disconnect();
      } else if (reconnectAttempts.current >= maxReconnectAttempts) {
        setConnectionError('Unable to connect to server. Please refresh the page.');
        newSocket.disconnect();
      } else {
        setConnectionError(`Connection error. Retrying... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
      }
    });

    newSocket.on('error', (error) => {
      console.error('WebSocket error:', error);
      setConnectionError('WebSocket error occurred');
    });

    // Ping-pong to keep connection alive
    const pingInterval = setInterval(() => {
      if (newSocket.connected) {
        newSocket.emit('ping');
      }
    }, 30000); // Every 30 seconds

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Cleanup function
    return () => {
      clearInterval(pingInterval);
      if (socketRef.current) {
        console.log('Cleaning up WebSocket connection');
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
    };
  }, [user]);

  // Reconnect on token refresh
  useEffect(() => {
    if (socket && user) {
      const handleTokenRefresh = () => {
        const newToken = getAuthToken();
        if (newToken && socket.connected) {
          // Update auth token for reconnection
          socket.auth = { token: newToken };
        }
      };

      // Listen for token refresh events if you have them
      window.addEventListener('auth:token-refreshed', handleTokenRefresh);
      
      return () => {
        window.removeEventListener('auth:token-refreshed', handleTokenRefresh);
      };
    }
  }, [socket, user]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, connectionError }}>
      {children}
    </SocketContext.Provider>
  );
};